import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoPipe } from '@jsverse/transloco';
import { ConfirmationDialogNewComponent } from 'libs/shared/ui-common/src/lib/components/dialog/confirmation-dialog-new/confirmation-dialog-new.component';
import { DialogService } from 'libs/shared/ui-common/src/lib/components/dialog/dialog.service';
import { filter, pairwise } from 'rxjs';
import { UserService } from 'shared/domain';
import { RichTextEditorComponent } from 'shared/feature-rich-text-editor';
import { IconDirective } from 'shared/ui-common';
import { AutofocusDirective, isTruthy } from 'shared/util-common';

import { ButtonDirective } from '../../../../../ui-common/src/lib/components/button/button.directive';
import { Comment, CommentType } from '../../comment.model';
import { CommentService } from '../../comment.service';
import { CommentCardCommentComponent } from './comment-card-comment/comment-card-comment.component';

@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'tt-comment-card',
  templateUrl: './comment-card.component.html',
  styleUrls: ['./comment-card.component.scss'],
  imports: [
    MatIconModule,
    IconDirective,
    TranslocoPipe,
    RichTextEditorComponent,
    AutofocusDirective,
    CommentCardCommentComponent,
    ButtonDirective,
  ],
})
export class CommentCardComponent {
  protected readonly CommentType = CommentType;
  protected userService = inject(UserService);
  private dialog = inject(DialogService);
  private commentService = inject(CommentService, { optional: true });

  public comment = input.required<Comment>();
  public readonly = input(false);
  public path = input<string | undefined>(undefined);

  public commentEdited = output<Comment>();
  public commentDeleted = output<Comment>();
  public replyDeleted = output<{ commentId: string; replyId: string }>();
  public commentClosed = output<void>();
  public replyToComment = output<{ commentId: string; reply: string }>();

  protected editMode = signal(false);

  protected tempReply = signal<string | undefined>(undefined);
  protected showReplies = signal(false);

  protected currentUser = this.userService.user;
  protected originalContentUnavailable = computed(() => this.commentService?.isOriginalContentUnavailable(this.comment()) ?? false);
  protected anchorThreadPosition = computed(() => {
    const commentId = this.comment().id;
    return commentId ? this.commentService?.getAnchorThreadPosition(commentId) : null;
  });

  constructor() {
    toObservable(this.comment)
      .pipe(
        pairwise(),
        filter(([prev, curr]) => prev.id !== curr.id),
      )
      .subscribe(() => {
        this.showReplies.set(false);
        this.tempReply.set(undefined);
        this.editMode.set(false);
      });
  }

  protected handleReplyComment() {
    if (this.readonly()) {
      return;
    }

    this.showReplies.set(true);
    this.tempReply.set('');
  }

  protected sendReply() {
    if (this.readonly()) {
      return;
    }

    const reply = this.tempReply() ?? '';
    if (reply.trim().length > 0) {
      this.replyToComment.emit({
        commentId: this.comment().id,
        reply: reply.trim(),
      });
    }

    this.showReplies.set(true);

    this.tempReply.set(undefined);
  }

  protected handleResolveComment(unresolve = false) {
    if (this.readonly()) {
      return;
    }

    this.commentEdited.emit({
      ...this.comment(),
      resolved: !unresolve,
    });
  }

  protected handleDeleteComment(comment: Comment, confirmationNeeded: boolean) {
    if (this.readonly()) {
      return;
    }

    if (!confirmationNeeded) {
      this.commentDeleted.emit(this.comment());
      return;
    }

    this.dialog
      .open(ConfirmationDialogNewComponent, {
        title: comment.type === CommentType.PersonalNote ? 'Comments.DeletePersonalNoteDialog.Title' : 'Comments.DeleteCommentDialog.Title',
        data: {
          text:
            comment.type === CommentType.PersonalNote
              ? 'Comments.DeletePersonalNoteDialog.Message'
              : 'Comments.DeleteCommentDialog.Message',
          confirmButton: 'Delete',
        },
      })
      .afterClosed()
      .pipe(isTruthy())
      .subscribe(() => {
        this.commentDeleted.emit(comment);
      });
  }

  protected handleCommentClosed() {
    if (!this.comment().createdAt) {
      this.commentDeleted.emit(this.comment());
    }

    this.commentClosed.emit();
  }
}
